import { z } from "zod";

const SPANISH_IBAN_PATTERN = /^ES\d{22}$/;
const SPANISH_CCC_PATTERN = /^\d{20}$/;
const SPAIN_COUNTRY_NUMERIC_CODE = "1428";
const hubSpotScalarInputSchema = z.union([z.string(), z.number()]).nullable().optional();
type HubSpotScalarInput = z.input<typeof hubSpotScalarInputSchema>;
export const SPANISH_IBAN_COMPACT_LENGTH = 24;
export const SPANISH_IBAN_DISPLAY_LENGTH = 28;

export function normalizeHubSpotIban(value: HubSpotScalarInput): string {
  const parsed = hubSpotScalarInputSchema.safeParse(value);
  const text = parsed.success ? String(parsed.data ?? "") : "";
  return text
    .toUpperCase()
    .replace(/[\s-]+/g, "")
    .trim();
}

export function normalizeSpanishIbanInput(value: HubSpotScalarInput): string {
  return normalizeHubSpotIban(value).slice(0, SPANISH_IBAN_COMPACT_LENGTH);
}

export function formatSpanishIban(value: HubSpotScalarInput): string {
  const normalized = normalizeHubSpotIban(value);
  const groups = [
    normalized.slice(0, 4),
    normalized.slice(4, 8),
    normalized.slice(8, 12),
    normalized.slice(12, 14),
    normalized.slice(14, 24),
    normalized.slice(24),
  ];
  return groups.filter(Boolean).join(" ");
}

export function hasSpanishIbanFormat(value: HubSpotScalarInput): boolean {
  return SPANISH_IBAN_PATTERN.test(normalizeHubSpotIban(value));
}

export function hasValidIbanChecksum(value: HubSpotScalarInput): boolean {
  const iban = normalizeHubSpotIban(value);
  if (!SPANISH_IBAN_PATTERN.test(iban)) {
    return false;
  }

  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
  let remainder = 0;
  for (const character of rearranged) {
    const numeric = character >= "A" && character <= "Z"
      ? String(character.charCodeAt(0) - 55)
      : character;
    for (const digit of numeric) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}

export function canonicalSpanishIbanFromLegacyCcc(value: HubSpotScalarInput): string {
  const ccc = normalizeHubSpotIban(value);
  if (!SPANISH_CCC_PATTERN.test(ccc)) {
    return "";
  }

  let remainder = 0;
  for (const digit of `${ccc}${SPAIN_COUNTRY_NUMERIC_CODE}00`) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  const checkDigits = String(98 - remainder).padStart(2, "0");
  return `ES${checkDigits}${ccc}`;
}
